import React from 'react';
import { datadogRum } from '@datadog/browser-rum';
import FastLinkIntegration, {
  YodleeSuccessData,
  YodleeExitData,
} from '@/components/yodlee/FastLinkIntegration';
import type { IInstitution } from '@/modules/portfolio/accountModal/accountTypeData';
import {
  AggregatorFormStatus,
  AggregatorModel,
  useLoadYodleeAccountsMutation,
  useUpdateYodleeCredentialsMutation,
  YodleeToken,
} from '@/store/yodleeApi';
import type { AppDispatch } from '@/store/store';
import { useDispatch } from 'react-redux';
import { accountsApi } from '@/store/accountsApi';
import { wealthsApi } from '@/store/wealthsApi';
import { useGlobalSnackbar } from '@/contexts/snackContext';
import { useLocalization } from '@/hooks/useLocalization';
import type { AccountConnectionError } from '@/hooks/useAccountConnectionError';

interface ReconnectYodleeIntegrationProps {
  institutionName: string;
  connectionError: AccountConnectionError;
  onProcessing: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onError: () => void;
}

const isConnected = (model: AggregatorModel | undefined): boolean =>
  model?.CredentialsForm?.Status.StatusCode === AggregatorFormStatus.Success;

export const ReconnectYodleeIntegration: React.FC<
  ReconnectYodleeIntegrationProps
> = ({
  institutionName,
  connectionError,
  onProcessing,
  onComplete,
  onCancel,
  onError,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useGlobalSnackbar();
  const { t } = useLocalization();
  const [updateYodleeCredentials] = useUpdateYodleeCredentialsMutation();
  const [loadYodleeAccounts] = useLoadYodleeAccountsMutation();

  const institution: IInstitution = {
    label: institutionName,
    value: String(connectionError.providerInstitutionId),
    url: '',
    institution: {
      InstitutionId: String(connectionError.providerInstitutionId),
      FinancialInstitutionId: String(connectionError.financialInstitutionId),
    },
  };

  const handleSuccess = async (
    data: YodleeSuccessData,
    _institution: IInstitution,
    yodleeToken: YodleeToken,
  ): Promise<void> => {
    try {
      const providerAccountId = Number(
        data.sites?.[0]?.providerAccountId ?? data.providerAccountId,
      );

      if (!providerAccountId) {
        throw new Error(
          'invalid Yodlee success payload (no providerAccountId)',
        );
      }

      // Must be the update (PUT) path. The create (POST) path leaves an
      // already-existing wealth untouched, so its stored error status survives
      // the reconnect and the icon never clears (EUD-797). This call is the
      // only one that resets it, and it does so synchronously.
      const credentialsResult = await updateYodleeCredentials({
        WealthId: connectionError.wealthId,
        FinancialInstitutionId: connectionError.financialInstitutionId,
        ProviderAccountId: providerAccountId,
        AuthToken: yodleeToken.token,
      });

      if (credentialsResult.error || !credentialsResult.data.WealthId) {
        throw new Error('Yodlee credentials update failed.');
      }

      const accountsResult = await loadYodleeAccounts({
        wealthId: credentialsResult.data.WealthId,
        YodleeToken: yodleeToken.token,
        IsUpdate: true,
      });

      if (accountsResult.error || !isConnected(accountsResult.data)) {
        // Best-effort: FastLink already confirmed the re-auth, the credentials
        // PUT persisted it, and that same PUT already cleared the stored error
        // status. This call only refreshes balances. On the update path the
        // backend can still return MoreInfoRequired from a stale Yodlee status
        // snapshot or an incomplete aggregation — neither means the
        // reconnection failed, so the flow must still complete (EUD-773).
        // Balances then land on the next refresh. The HTTP 500 that Yodlee's
        // post-aggregation ineligibility used to cause is fixed in EUD-804:
        // the backend now reads the data Yodlee already holds instead of
        // throwing.
        // Only scalar diagnostics — the raw AggregatorModel payload can carry
        // an AuthToken and must never reach the console or RUM.
        const reloadFailure = {
          wealthId: credentialsResult.data.WealthId,
          providerAccountId,
          statusCode:
            accountsResult.data?.CredentialsForm?.Status.StatusCode ?? null,
          isHttpError: Boolean(accountsResult.error),
        };
        console.warn(
          '[ReconnectYodleeIntegration] accounts reload failed after a successful reconnect',
          reloadFailure,
        );
        datadogRum.addError(
          new Error('Yodlee accounts reload failed after successful reconnect'),
          reloadFailure,
        );
      }

      // The PUT above cleared the error status synchronously, so refetching is
      // what surfaces the real state — error flag, balances and last-updated
      // together. Both slices need their own invalidation: they are separate
      // createApi instances and do not share a tag registry.
      dispatch(
        wealthsApi.util.invalidateTags([{ type: 'Wealths', id: 'LIST' }]),
      );
      dispatch(
        accountsApi.util.invalidateTags([{ type: 'Accounts', id: 'LIST' }]),
      );

      showSnackbar(
        t('connectionError.reconnectSuccess', { name: institutionName }),
      );
      onComplete();
    } catch (error) {
      console.error('[ReconnectYodleeIntegration] failed', error);
      datadogRum.addError(error);
      onError();
    }
  };

  const handleClose = (
    data: YodleeExitData,
    institution: IInstitution,
    yodleeToken: YodleeToken,
  ): void => {
    if (
      data.fnToCall === 'accountStatus' &&
      data.status !== 'USER_CLOSE_ACTION' &&
      data.sites &&
      data.sites[0] &&
      data.sites[0].status !== 'FAILED'
    ) {
      void handleSuccess(data, institution, yodleeToken);
      return;
    }

    // Any close that is not a success payload — USER_CLOSE_ACTION, ambiguous
    // exit, or sites[0].status === 'FAILED' — is treated as a benign cancel;
    // the user returns to the message view which still shows the unresolved error.
    onCancel();
  };

  const handleError = (): void => {
    onError();
  };

  const fastLinkParams = connectionError.externalId
    ? {
        flow: connectionError.flow,
        providerAccountId: connectionError.externalId,
      }
    : {
        flow: connectionError.flow,
      };

  return (
    <FastLinkIntegration
      institution={institution}
      params={fastLinkParams}
      onSuccess={handleSuccess}
      onError={handleError}
      onClose={handleClose}
      onSessionProcessing={onProcessing}
      onExternalOAuthReturn={onProcessing}
      onExternalOAuthTimeout={onError}
    />
  );
};
